import React, { FC, useCallback } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { useReactForm, VestingTaskHooks } from '../../../hooks/index';
import { SharePlanType, sharePlanTypeTitle } from '../../../types/pool-plans.types';
import { handleFormInvalid } from '../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../utils/to-backend-date-format.util';
import { FormSchema, formSchema } from './exercise-form.validation';
import { StepOne } from './step-one';

export type ExerciseFormProps = {
  companyId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpen: boolean;
  sharePlanId: string;
  type: SharePlanType;
};

export const ExerciseForm: FC<ExerciseFormProps> = ({
  isOpen,
  companyId,
  handleClose,
  invalidateQuery,
  type,
}) => {
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    getFieldState,
    setError,
    clearErrors,
    resetField,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
  });
  const { requestAsync, isPending } = VestingTaskHooks.useRequestExercise();

  const handleCloseModal = useCallback(() => {
    reset();
    handleClose();
  }, [reset, handleClose]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { stepOne } = data;

      // const rsa =
      //   type === SharePlanType.RSA &&
      //   stepOne.rsa?.rsaItems &&
      //   stepOne.rsa?.rsaItems.length > 0 &&
      //   stepOne.rsa?.rsaItems.map(({ exercised, grant }) => ({
      //     vestingTaskId: grant.id,
      //     value: exercised,
      //     filesLinks: [],
      //   }));

      const stock =
        type === SharePlanType.STOCK_OPTIONS &&
        stepOne.commonStock?.stockItems &&
        stepOne.commonStock.stockItems.length > 0 &&
        stepOne.commonStock?.stockItems.map(({ exercised, grant, date }) => ({
          vestingTaskId: grant.id,
          value: exercised,
          date: toBackendDateFormat(date),
          filesLinks: [],
        }));

      const warrantExercise =
        type === SharePlanType.WARRANTS &&
        stepOne.warrantExercise?.warrantExerciseItems &&
        stepOne.warrantExercise.warrantExerciseItems.length > 0 &&
        stepOne.warrantExercise?.warrantExerciseItems.map(({ exercised, grant, date }) => ({
          vestingTaskId: grant.id,
          value: exercised,
          date: toBackendDateFormat(date),
          filesLinks: [],
        }));

      const sar =
        type === SharePlanType.SAR &&
        stepOne.sar?.sarItems &&
        stepOne.sar?.sarItems.length > 0 &&
        stepOne.sar?.sarItems.map(({ sarCount, grant, sarPayout, sharePrice, date }) => ({
          vestingTaskId: grant.id,
          value: sarCount,
          payout: sarPayout,
          sarSharePrice: sharePrice,
          date: toBackendDateFormat(date),
          filesLinks: [],
        }));

      const hurdle =
        type === SharePlanType.GROWTH_HURDLE &&
        stepOne.hurdle?.hurdleItems &&
        stepOne.hurdle?.hurdleItems.length > 0 &&
        stepOne.hurdle?.hurdleItems
          .filter(({ selected }) => selected)
          .map(({ issuedShares, grantId: vestingTaskId, date }) => ({
            vestingTaskId,
            value: issuedShares,
            date: toBackendDateFormat(date),
            filesLinks: [],
          }));

      const bspce =
        type === SharePlanType.BSPCE &&
        stepOne.bspce?.bspceItems &&
        stepOne.bspce?.bspceItems.length > 0 &&
        stepOne.bspce?.bspceItems.map(({ exercised, grant, date }) => ({
          vestingTaskId: grant.id,
          value: exercised,
          date: toBackendDateFormat(date),
          filesLinks: [],
        }));

      const exerciseData = stock || warrantExercise || sar || hurdle || bspce;
      if (!exerciseData || exerciseData.length === 0) return;

      // One backend request per item — the hurdle step is a multi-select, so submitting only
      // the first item would silently drop the rest. Sequential awaits: the idempotent
      // mutation rejects concurrent submits. A mid-list failure keeps the modal open; the
      // hook already toasts the error.
      const submitAll = async () => {
        for (const item of exerciseData) {
          await requestAsync({ companyId, data: item });
        }
        toast.success(<AppFormattedMessage id="EXERCISE_REQUESTED" />);
        invalidateQuery();
        handleCloseModal();
      };

      void submitAll().catch(() => undefined);
    },
    [companyId, requestAsync, handleCloseModal, invalidateQuery, type],
  );

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpen}>
      <SheetContent
        className={twMerge(
          'h-fit max-h-[800px] w-[90%] max-w-[1422px] border-transparent bg-transparent p-2 shadow-none',
          type === SharePlanType.GROWTH_HURDLE && 'w-fit min-w-[490px]',
        )}
        onInteractOutside={handleCloseModal}
        side="CENTER"
      >
        <div className="flex h-full w-full flex-col gap-8 overflow-hidden rounded-2xl bg-gray-25 p-8">
          <div className="flex h-fit w-full items-center justify-between">
            <span className="text-xl font-semibold text-gray-700">
              <AppFormattedMessage id="REQUEST_EXERCISE" /> -{' '}
              {<AppFormattedMessage id={sharePlanTypeTitle[type]} />}
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex h-full max-h-[520px] flex-col overflow-y-auto">
            <StepOne
              clearErrors={clearErrors}
              companyId={companyId}
              control={control}
              errors={errors}
              filedState={getFieldState}
              formData={getValues}
              handleCloseModal={handleCloseModal}
              isLoading={isPending}
              reset={reset}
              resetField={resetField}
              setError={setError}
              setFormData={setValue}
              submitForm={() => handleSubmit(submitHandler, handleFormInvalid)()}
              type={type}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
